import { ASSISTANT_VIA_HEADER, type AssistantChatRequest, type AssistantSettings, type AssistantStreamEvent, type UpdateAssistantSettingsRequest } from '@twinlink/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, apiFetch } from '@web/lib/api';
import { fill, type ActionBlock } from './model';

const settingsKey = ['platform', 'assistant'] as const;

/**
 * Asks Dyia. The answer comes as lines of JSON (doc 13 §7): `onEvent` is called for each one, as
 * it arrives. A refusal (not available, daily limit) is an ordinary error and rejects.
 */
async function askDyia(orgId: string, body: AssistantChatRequest, onEvent: (event: AssistantStreamEvent) => void): Promise<void> {
  const res = await apiFetch(`/orgs/${orgId}/assistant/chat`, { method: 'POST', body });
  const reader = res.body?.getReader();
  if (!reader) throw new Error('No answer');
  const decoder = new TextDecoder();
  let buffer = '';
  let finished = false;
  const take = (line: string) => {
    if (!line.trim()) return;
    const event = JSON.parse(line) as AssistantStreamEvent;
    if (event.t === 'error') throw new Error(event.message);
    if (event.t === 'done') finished = true;
    onEvent(event);
  };
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() ?? '';
    lines.forEach(take);
  }
  take(buffer);
  // A connection cut in the middle: what was written stays, the turn is marked as failed.
  if (!finished) throw new Error('The answer was cut off.');
}

export const useAskDyia = (orgId: string) =>
  useMutation({
    mutationFn: ({ body, onEvent }: { body: AssistantChatRequest; onEvent: (event: AssistantStreamEvent) => void }) => askDyia(orgId, body, onEvent),
  });

/**
 * Makes the changes of a card the user confirmed: the steps go through the ordinary routes, in
 * order, as the user. It stops at the first refusal; `onStep` says how many are done.
 */
export function useRunAction(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ action, onStep }: { action: ActionBlock; onStep: (done: number) => void }): Promise<string[]> => {
      const ids: string[] = [];
      try {
        for (const [i, step] of action.steps.entries()) {
          const res = await api<{ id?: string } | undefined>(`/orgs/${orgId}${fill(step.path, ids)}`, { method: step.method, body: fill(step.body, ids), headers: { [ASSISTANT_VIA_HEADER]: 'dyia' } });
          ids.push(res?.id ?? '');
          onStep(i + 1);
        }
      } finally {
        // Whatever was made shows at once on the pages behind the chat.
        void qc.invalidateQueries({ queryKey: ['org', orgId] });
      }
      return ids;
    },
  });
}

export const useAssistantSettings = () => useQuery({ queryKey: settingsKey, queryFn: () => api<AssistantSettings>('/platform/assistant') });

export function useUpdateAssistantSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateAssistantSettingsRequest) => api<AssistantSettings>('/platform/assistant', { method: 'PATCH', body }),
    onSuccess: (data) => {
      qc.setQueryData(settingsKey, data);
      // Whether Dyia shows in the top bar comes with the organization context.
      void qc.invalidateQueries({ queryKey: ['org'] });
    },
  });
}
